import React, { useMemo } from "react";
import { NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Sparkles,
  Inbox,
  CalendarDays,
  Library,
  Wand2,
  Building2,
  Plug,
  Settings,
  FileText,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAppState } from "@/state/AppStateProvider";

export const PRIMARY = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/create", label: "Create", icon: Sparkles },
  { to: "/library", label: "Library", icon: Library },
  { to: "/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/blog", label: "Blog Studio", icon: FileText },
  // Projects hidden for MVP — read-only/unwired; see docs/planned-integrations.md (v2).
  { to: "/inbox", label: "Content Inbox", icon: Inbox },
];

export const SETTINGS = [
  { to: "/brands", label: "Brands", icon: Building2 },
  { to: "/skills", label: "Skills", icon: Wand2 },
  { to: "/integrations", label: "Integrations", icon: Plug },
  { to: "/settings", label: "Settings", icon: Settings },
];

export function NavItem({ item, collapsed, onNavigate }) {
  const Icon = item.icon;
  const content = (
    <NavLink
      to={item.to}
      onClick={() => onNavigate?.()}
      data-testid={`sidebar-nav-item-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        cn(
          "group relative flex h-10 items-center gap-3 rounded-lg px-3 text-sm font-medium transition-all duration-150",
          isActive
            ? "bg-[color:var(--chrome-2)] text-[color:var(--chrome-text)] shadow-[inset_2px_0_0_var(--accent-blue)]"
            : "text-[color:var(--chrome-text-muted)] hover:bg-[color:var(--chrome-hover)] hover:text-[color:var(--chrome-text)]",
          collapsed && "justify-center px-0",
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={cn("h-4 w-4 shrink-0", isActive ? "text-[color:var(--accent-blue)]" : "text-[color:var(--chrome-text-muted)] group-hover:text-[color:var(--chrome-text)]")} />
          {!collapsed && <span className="truncate">{item.label}</span>}
          {!collapsed && item.badge ? (
            <span className="ml-auto font-mono text-[10px] font-semibold text-[color:var(--accent-blue)]">
              {item.badge}
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  );
  if (!collapsed) return content;
  return (
    <Tooltip delayDuration={50}>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent side="right" className="text-xs">
        {item.label}
      </TooltipContent>
    </Tooltip>
  );
}

export function SidebarBody({ collapsed = false, onNavigate, showQuick = true }) {
  const { state } = useAppState();
  const inboxActiveCount = useMemo(
    () => (state.inbox || []).filter((e) => e.status !== "archived").length,
    [state.inbox],
  );

  const primary = useMemo(
    () => PRIMARY.map((item) =>
      item.to === "/inbox" && inboxActiveCount > 0
        ? { ...item, badge: String(inboxActiveCount) }
        : item,
    ),
    [inboxActiveCount],
  );

  return (
    <TooltipProvider>
      <nav className="flex-1 overflow-y-auto scrollbar-thin px-2 py-3">
        {!collapsed && (
          <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-[color:var(--chrome-text-muted)]">Workspace</p>
        )}
        <div className="flex flex-col gap-0.5">
          {primary.map((it) => <NavItem key={it.to} item={it} collapsed={collapsed} onNavigate={onNavigate} />)}
        </div>

        <div className="my-4 h-px bg-[color:var(--chrome-border)]" />
        {!collapsed && (
          <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-[color:var(--chrome-text-muted)]">System</p>
        )}
        <div className="flex flex-col gap-0.5">
          {SETTINGS.map((it) => <NavItem key={it.to} item={it} collapsed={collapsed} onNavigate={onNavigate} />)}
        </div>
      </nav>
    </TooltipProvider>
  );
}
